import * as React from "react";

import { BabylonManager } from "@/shared/components/visualization/Panel/ThreeDPanel/BabylonManager";
import { useBabylonTheme } from "@/shared/components/visualization/Panel/ThreeDPanel/BabylonManager/useBabylonTheme";
import { DataServerManager } from "@/shared/components/visualization/Panel/ThreeDPanel/DataServer/DataServerManager";
import type { ModelAssetLoader } from "@/shared/components/visualization/Panel/ThreeDPanel/ModelLoader";
import { isAbortError } from "@/shared/errors";
import { ErrorMonitoringService } from "@/shared/services";
import type { ThreeDPanelState } from "@/shared/state/visualization";

import styles from "./BabylonPreview.module.css";

interface BabylonPreviewProps {
  /**
   * The scene to draw, at its rest pose. Every change is pushed straight to the
   * live scene — but joints are not posed through here; see
   * {@link BabylonPreviewProps.onManagerChanged}.
   */
  state: ThreeDPanelState;
  modelLoader: ModelAssetLoader;
  /**
   * Hands the live manager out, and `null` whenever there is none — before the
   * canvas is up, after it is torn down, and on a machine where the engine could
   * not be built at all.
   *
   * A joint's live value is renderer state, not panel state, so posing one is an
   * imperative call on the manager rather than a new `state` prop. This is how a
   * caller reaches it. Called with the same manager again if its identity
   * changes, so it should be stable or idempotent.
   */
  onManagerChanged?: (manager: BabylonManager | null) => void;
}

/**
 * A Babylon canvas showing one static pose of a model.
 *
 * Mounts **`BabylonManager` directly rather than `ThreeDPanel`**: the panel
 * calls `useWorkspaceTimer()` and renders `PanelLayout`, both of which need the
 * ephemeral-workspace context and drag-and-drop providers that a modal does not
 * have. The manager itself needs only a canvas, a time, a theme, callbacks,
 * state, a model loader and a data server.
 *
 * The time is pinned at zero and never advances. Nothing in this scene binds a
 * topic — every position and rotation is a constant — so there is no timeline to
 * follow. The sliders do not write those constants: they drive the manager's
 * joint-pose components directly, through the manager this component publishes.
 *
 * **Known cost:** `DataServerManager.create()` is not optional. It is `readonly`
 * on the manager's context and `dispose()` calls into it, so the modal spins up
 * a real Worker even though a constant-pose model never queries topic data.
 *
 * Engine construction is the one unrecoverable failure (typically "WebGL not
 * supported"), and it is caught rather than thrown: the joint list beside this
 * canvas is still worth showing, and a user on a machine without WebGL should
 * be told why the viewport is empty.
 */
export function BabylonPreview({
  state,
  modelLoader,
  onManagerChanged,
}: BabylonPreviewProps) {
  const theme = useBabylonTheme();
  const canvasRef = React.useRef<HTMLCanvasElement>();
  const [manager, setManager] = React.useState<BabylonManager | null>(null);
  const [error, setError] = React.useState<Error | null>(null);
  const [dataServer, setDataServer] = React.useState<DataServerManager | null>(
    null,
  );

  // Read once at construction: the manager owns them from then on, and the
  // effects below push every subsequent change in.
  const initState = React.useRef(state);
  const initTheme = React.useRef(theme);
  const initModelLoader = React.useRef(modelLoader);

  React.useEffect(function initDataServer() {
    const controller = new AbortController();
    DataServerManager.create(controller)
      .then((created) => {
        setDataServer(created);
      })
      .catch((e: unknown) => {
        if (!controller.signal.aborted && !isAbortError(e)) {
          ErrorMonitoringService.captureError(e);
        }
      });
    return () => {
      controller.abort();
    };
  }, []);

  React.useEffect(
    function init() {
      const canvas = canvasRef.current;
      if (canvas === undefined || dataServer === null) {
        return;
      }

      let created: BabylonManager | null = null;
      try {
        created = new BabylonManager({
          dataServer,
          modelLoader: initModelLoader.current,
          canvas,
          time: 0n,
          initialState: initState.current,
          theme: initTheme.current,
          callbacks: {
            onLoadingStatusChanged: () => {},
            onModelLoadFailed: (_modelId, modelError) => {
              setError(modelError);
            },
          },
        });
        setManager(created);
      } catch (e) {
        // Normally "WebGL not supported", which no retry can fix.
        ErrorMonitoringService.captureError(e);
        setError(e as Error);
        return;
      }

      return () => {
        setManager(null);
        created?.dispose();
      };
    },
    [dataServer],
  );

  React.useEffect(
    function onStateUpdated() {
      try {
        manager?.setState(state);
      } catch (e) {
        ErrorMonitoringService.captureError(e);
      }
    },
    [state, manager],
  );

  React.useEffect(
    function publishManager() {
      onManagerChanged?.(manager);
    },
    [manager, onManagerChanged],
  );

  React.useEffect(
    function updateTheme() {
      manager?.setTheme(theme);
    },
    [theme, manager],
  );

  return (
    <div className={styles.viewport}>
      <canvas
        aria-label="Model preview"
        className={styles.canvas}
        ref={(ref) => (canvasRef.current = ref ?? undefined)}
      />
      {error && (
        <p className={styles.viewportError} role="status">
          This model cannot be rendered here: {error.message}
        </p>
      )}
    </div>
  );
}
